import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../contexts/AuthContext";
import { useReportLists } from "../hooks/useReportLists";
import AuthForm from "./AuthForm";
import BusinessProfileDialog from "./BusinessProfileDialog";
import Footer from "./Footer";
import Navbar from "./Navbar";
import LeadModal from "./landing/LeadModal";
import { ReportsSidebar } from "./reports/ReportsSidebar";

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, businessProfile, updateBusinessProfile, logout } = useAuth();
  const { reports, loading, refresh } = useReportLists(user);
  const [showSidebar, setShowSidebar] = useState(false);

  const [showBusinessProfile, setShowBusinessProfile] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const authMode = new URLSearchParams(location.search).get("auth");
  const showAuthForm = authMode === "login" || authMode === "signup";
  const buildUrlWithParams = (updates = {}, removals = []) => {
    const params = new URLSearchParams(location.search);
    removals.forEach((key) => params.delete(key));
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") params.delete(key);
      else params.set(key, value);
    });
    const search = params.toString();
    return `${location.pathname}${search ? `?${search}` : ""}${location.hash || ""}`;
  };
  const openAuthModal = (mode, options = {}) =>
    navigate(
      buildUrlWithParams({
        auth: mode,
        next: options.next,
        selected_plan: options.selectedPlan,
      }),
    );

  useEffect(() => {
    const toggle = () => setShowSidebar((open) => !open);
    const openProfile = () => setShowBusinessProfile(true);
    window.addEventListener("toggleSidebar", toggle);
    window.addEventListener("openBusinessProfile", openProfile);
    return () => {
      window.removeEventListener("toggleSidebar", toggle);
      window.removeEventListener("openBusinessProfile", openProfile);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background text-on-surface font-body">
      {showAuthForm && (
        <AuthForm
          initialMode={authMode}
          onAuthSuccess={() => {
            window.dispatchEvent(new CustomEvent("authChange"));
            const next = new URLSearchParams(location.search).get("next");
            navigate(next || buildUrlWithParams({}, ["auth", "next"]), { replace: true });
          }}
          onCancel={() => navigate(buildUrlWithParams({}, ["auth", "next"]), { replace: true })}
        />
      )}
      <LeadModal open={showLeadModal} onOpenChange={setShowLeadModal} />
      {showSidebar && (
        <button
          aria-label="Close reports sidebar"
          className="fixed inset-0 z-[60] bg-black/40"
          onClick={() => setShowSidebar(false)}
        />
      )}
      <ReportsSidebar
        open={showSidebar}
        user={user}
        loading={loading}
        reports={reports}
        onClose={() => setShowSidebar(false)}
        onRefresh={refresh}
        onLogin={() => {
          setShowSidebar(false);
          openAuthModal("login");
        }}
      />
      <Navbar
        onAuthClick={openAuthModal}
        onLogout={() => {
          logout();
          window.dispatchEvent(new CustomEvent("authChange"));
        }}
        onToggleSidebar={() => setShowSidebar((open) => !open)}
        onOpenBusinessProfile={() => setShowBusinessProfile(true)}
      />
      {user && (
        <BusinessProfileDialog
          open={showBusinessProfile || businessProfile?.is_complete === false}
          onOpenChange={setShowBusinessProfile}
          prefillDomain={reports.audits[0]?.url ?? ""}
          onSaved={(profile) => {
            updateBusinessProfile(profile);
            setShowBusinessProfile(false);
          }}
          mandatory={businessProfile?.is_complete === false}
        />
      )}
      <main className="pt-16">{children}</main>
      <Footer onContactUs={() => setShowLeadModal(true)} />
    </div>
  );
};

export default Layout;
